import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Checkbox, DatePicker, Button, Space, Typography, Statistic, Row, Col, Tag, Spin, Alert, Table, message } from 'antd';
import { DownloadOutlined, SearchOutlined, ReloadOutlined, CaretRightOutlined, CaretDownOutlined, EditOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { usePointOfSaleService } from '../../services/pointofsale/PointOfSaleServiceProvider';
import { useMemberService } from '../../services/members/MemberServiceProvider';
import { usePaymentService } from '../../services/payments/PaymentServiceProvider';
import { useReceiptPrinterService } from '../../services/printing/ReceiptPrinterServiceProvider';
import { ReceiptPrinterService } from '../../services/printing/ReceiptPrinterService';
import { PointOfSaleTransaction } from '../../model/pointofsale/PointOfSaleTransaction';
import { DisplayDirectDebitTransaction } from '../../model/payments/DisplayDirectDebitTransaction';
import { MemberProfile } from '../../model/members/MemberProfile';
import UpdateTransactionModal from './UpdateTransactionModal';
import { useFormatCurrency, useCurrencySymbol } from '../../hooks/useFormatters';
import { formatNumber } from '../../utils/formatters';

const { Title } = Typography;

// Reconciliation category constants
const CATEGORY_SQUASH = 'Squash';
const CATEGORY_MEMBERSHIPS_DD = 'Memberships - Direct Debit';
const CATEGORY_MEMBERSHIPS_MANUAL = 'Memberships - Manual Payments';
const CATEGORY_BAR = 'Bar';
const CATEGORY_SHOP = 'Shop';
const CATEGORY_STUDIO = 'Studio Hire';
const CATEGORY_TREATMENTS = 'Treatments';
const CATEGORY_OTHER = 'Other';
const CATEGORY_ADMIN = 'Admin Bookings';
const ADMIN_MEMBERSHIP_ID = '000001';

// ---------------------------------------------------------------------------
// Payment status helpers
// Completed  — payment received and settled
// Unpaid     — awaiting manual payment
// Pending    — payment in progress / awaiting collection
// Failed     — payment attempted but failed (DD rejection, card decline, etc.)
// Cancelled  — explicitly cancelled; excluded from reconciliation
// NotApplicable — not a payment item; excluded from reconciliation
// ---------------------------------------------------------------------------
const isCompleted  = (status: string) => status === 'Completed';
const isUnpaid     = (status: string) => status === 'Unpaid' || status === 'Pending';
const isFailed     = (status: string) => status === 'Failed';
const isOutstanding = (status: string) => isUnpaid(status) || isFailed(status);
const isExcluded   = (status: string) => status === 'Cancelled' || status === 'NotApplicable';

// Map DD-specific payment statuses onto the reconciliation status set
function mapDdStatus(ddStatus: string): string {
    if (ddStatus === 'Paid' || ddStatus === 'Completed') return 'Completed';
    if (ddStatus === 'Failed' || ddStatus === 'Rejected') return 'Failed';
    return ddStatus; // Pending, Cancelled, etc. pass through
}

const SQUASH_ITEM_NAMES = ['guest fee', 'club night', 'team squash', 'beginners squash'];
const TREATMENT_ITEM_NAMES = ['treatment 1', 'treatment room fee'];

interface TransactionItem {
    key: string;
    category: string;
    subCategory?: string;
    vatApplicable?: boolean;
    type: string;
    memberName: string;
    membershipId: string;
    amount: number;
    timestamp: Date;
    status: string;
    itemName: string;
    quantity: number;
    unitPrice: number;
    facilityName?: string;
    isBooking: boolean;
    isDDPayment: boolean;
    dateFormatted: string;
    timeFormatted: string;
    slotStartTime?: string;
    slotEndTime?: string;
    slotDateFormatted?: string;
}

interface GroupedData {
    category: string;
    totalAmount: number;
    completedAmount: number;
    unpaidAmount: number;
    failedAmount: number;
    itemCount: number;
    completedCount: number;
    unpaidCount: number;
    failedCount: number;
    items: TransactionItem[];
    subCategories: { [key: string]: { completedAmount: number; unpaidAmount: number; failedAmount: number; completedCount: number; unpaidCount: number; failedCount: number } };
}

function resolveCategory(
    itemType: string,
    itemName: string,
    isBooking: boolean,
    facilityType?: string
): { category: string; subCategory?: string } {
    // Facility bookings - categorize by facility type
    if (isBooking) {
        if (facilityType === 'Squash') {
            return { category: CATEGORY_SQUASH };
        }
        if (facilityType === 'Studio') {
            return { category: CATEGORY_STUDIO };
        }
        if (facilityType === 'TreatmentRoom') {
            return { category: CATEGORY_TREATMENTS };
        }
        return { category: CATEGORY_OTHER };
    }

    // Treatment POS items by name
    const nameLower = itemName.toLowerCase();
    if (TREATMENT_ITEM_NAMES.some(t => nameLower.includes(t))) {
        return { category: CATEGORY_TREATMENTS };
    }

    // Squash-related POS items by name
    if (SQUASH_ITEM_NAMES.some(s => nameLower.includes(s))) {
        return { category: CATEGORY_SQUASH };
    }

    // POS item type-based categorization
    if (itemType === 'Squash') {
        return { category: CATEGORY_SQUASH };
    }
    if (itemType === 'Bar') {
        return { category: CATEGORY_BAR };
    }
    if (itemType === 'Shop') {
        return { category: CATEGORY_SHOP };
    }
    if (itemType === 'Membership') {
        return { category: CATEGORY_MEMBERSHIPS_MANUAL };
    }

    return { category: CATEGORY_OTHER };
}

function resolveVatSubCategory(isVatApplicable?: boolean): string | undefined {
    if (isVatApplicable === true) return 'VAT Items';
    if (isVatApplicable === false) return 'Non-VAT Items';
    return undefined;
}

function resolveMembershipSubCategory(paymentFrequency: string): string {
    const freq = paymentFrequency.toLowerCase();
    if (freq === 'yearly' || freq === 'annual') return 'Annual Subs';
    if (freq === 'monthly') return 'Monthly Subs';
    return 'Other';
}

const JOINING_FEE_KEYWORDS = ['joining fee'];

function resolveManualMembershipSubCategory(itemName: string, memberPaymentFrequency?: string): string {
    const nameLower = itemName.toLowerCase();
    if (JOINING_FEE_KEYWORDS.some(k => nameLower.includes(k))) {
        return 'Joining Fees';
    }
    if (memberPaymentFrequency) {
        return resolveMembershipSubCategory(memberPaymentFrequency);
    }
    return 'Other';
}

// Category display order
const CATEGORY_ORDER = [
    CATEGORY_SQUASH,
    CATEGORY_MEMBERSHIPS_DD,
    CATEGORY_MEMBERSHIPS_MANUAL,
    CATEGORY_BAR,
    CATEGORY_SHOP,
    CATEGORY_STUDIO,
    CATEGORY_TREATMENTS,
    CATEGORY_OTHER,
    CATEGORY_ADMIN,
];

const TransactionReconciliation: React.FC = () => {
    const pointOfSaleService = usePointOfSaleService();
    const memberService = useMemberService();
    const paymentService = usePaymentService();
    const receiptPrinterService = useReceiptPrinterService();
    const formatCurrency = useFormatCurrency();
    const currencySymbol = useCurrencySymbol();
    const [transactions, setTransactions] = useState<PointOfSaleTransaction[]>([]);
    const [ddTransactions, setDdTransactions] = useState<DisplayDirectDebitTransaction[]>([]);
    const [members, setMembers] = useState<{ [key: string]: string }>({});
    const [memberFrequencies, setMemberFrequencies] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Dayjs>(dayjs());
    const [toDate, setToDate] = useState<Dayjs>(dayjs());
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const [expandedItemGroups, setExpandedItemGroups] = useState<string[]>([]);
    const [showUnpaidOnly, setShowUnpaidOnly] = useState(false);
    const [updateTransactionModalVisible, setUpdateTransactionModalVisible] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<PointOfSaleTransaction | null>(null);

    // Add CSS for unpaid rows
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .unpaid-row { background-color: #fff7e6 !important; }
            .failed-row { background-color: #fff1f0 !important; }
        `;
        document.head.appendChild(style);
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    // Fetch transactions
    const fetchTransactions = async (from?: Dayjs, to?: Dayjs) => {
        const fromDateToUse = from || fromDate;
        const toDateToUse = to || toDate;

        if (!fromDateToUse || !toDateToUse) return;

        const fromStr = fromDateToUse.format('YYYY-MM-DD');
        const toStr = toDateToUse.format('YYYY-MM-DD');

        setLoading(true);
        try {
            const [posData, ddData] = await Promise.all([
                pointOfSaleService.getPointOfSaleTransactionsByClient(fromStr, toStr),
                paymentService.getDirectDebitTransactions(fromStr, toStr).catch(error => {
                    console.error('Error fetching DD transactions:', error);
                    return [] as DisplayDirectDebitTransaction[];
                })
            ]);
            setTransactions(posData);
            setDdTransactions(ddData);
        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const profiles = await memberService.getMemberProfiles();
                const memberMap: { [key: string]: string } = {};
                const freqMap: { [key: string]: string } = {};
                profiles.forEach((member: MemberProfile) => {
                    memberMap[member.membershipId] = member.name;
                    if (member.membershipCategory?.paymentFrequency) {
                        freqMap[member.membershipId] = member.membershipCategory.paymentFrequency;
                    }
                });
                setMembers(memberMap);
                setMemberFrequencies(freqMap);
            } catch (error) {
                console.error('Error fetching members:', error);
            }
        };

        fetchMembers();
        fetchTransactions();
        // Initial load only; fetchTransactions/memberService are intentionally
        // excluded so this runs once on mount (date-range refetches are separate).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Flatten transactions into individual items with category resolution
    const transactionItems: TransactionItem[] = useMemo(() => {
        const items: TransactionItem[] = [];

        // POS transaction items and facility bookings (exclude Cancelled and NotApplicable)
        const activeTransactions = transactions.filter(t => !isExcluded(t.status));
        activeTransactions.forEach(transaction => {
            const memberName = members[transaction.membershipId] || `Unknown (${transaction.membershipId})`;
            const isAdminBooking = transaction.membershipId === ADMIN_MEMBERSHIP_ID;
            // Used to decide display date: unpaid/pending bookings show the slot date, not transaction date
            const useSlotDate = isUnpaid(transaction.status);

            // For pending transactions with bookings, use the booking date as the display date
            // because the transaction timestamp is when the booking was made, not when it's for
            const displayTimestamp = useSlotDate && transaction.bookingSlots.length > 0
                ? dayjs(transaction.bookingSlots[0].bookingSlot.startTime)
                : dayjs(transaction.timestamp);

            // Add POS items
            transaction.items.forEach((item, itemIndex) => {
                const { category: resolvedCategory, subCategory } = resolveCategory(
                    item.item.type,
                    item.item.name,
                    false
                );
                const category = isAdminBooking ? CATEGORY_ADMIN : resolvedCategory;

                const membershipSub = category === CATEGORY_MEMBERSHIPS_MANUAL
                    ? resolveManualMembershipSubCategory(item.item.name, memberFrequencies[transaction.membershipId])
                    : undefined;

                items.push({
                    key: `${transaction.transactionId}-${transaction.membershipId}-item-${itemIndex}`,
                    category,
                    subCategory: isAdminBooking ? resolvedCategory : (subCategory || membershipSub),
                    vatApplicable: item.item.vatApplies ?? true,
                    type: item.item.type,
                    memberName,
                    membershipId: transaction.membershipId,
                    amount: (item.overridePrice || item.item.price) * item.quantity,
                    timestamp: displayTimestamp.toDate(),
                    status: transaction.status,
                    itemName: item.item.name,
                    quantity: item.quantity,
                    unitPrice: item.overridePrice || item.item.price,
                    isBooking: false,
                    isDDPayment: false,
                    dateFormatted: displayTimestamp.format('DD/MM/YYYY'),
                    timeFormatted: displayTimestamp.format('HH:mm')
                });
            });

            // Add facility bookings
            // For pending transactions, only include booking slots that fall within the selected date range
            const slotsToInclude = useSlotDate
                ? transaction.bookingSlots.filter(slot => {
                    const slotDate = dayjs(slot.bookingSlot.startTime);
                    return slotDate.isSame(fromDate, 'day') || slotDate.isSame(toDate, 'day')
                        || (slotDate.isAfter(fromDate, 'day') && slotDate.isBefore(toDate, 'day'));
                })
                : transaction.bookingSlots;

            slotsToInclude.forEach((slot, slotIndex) => {
                const { category: resolvedBookingCategory } = resolveCategory(
                    'FacilityBookings',
                    '',
                    true,
                    slot.bookingSlot.facilityType
                );
                const bookingCategory = isAdminBooking ? CATEGORY_ADMIN : resolvedBookingCategory;

                const slotStart = dayjs(slot.bookingSlot.startTime);
                const slotEnd = dayjs(slot.bookingSlot.endTime);

                // For pending bookings, use the booking slot date as the display date
                const bookingDisplayDate = useSlotDate ? slotStart : dayjs(transaction.timestamp);

                items.push({
                    key: `${transaction.transactionId}-${transaction.membershipId}-booking-${slotIndex}`,
                    category: bookingCategory,
                    subCategory: isAdminBooking ? resolvedBookingCategory : undefined,
                    type: 'FacilityBookings',
                    memberName,
                    membershipId: transaction.membershipId,
                    amount: slot.price,
                    timestamp: bookingDisplayDate.toDate(),
                    status: transaction.status,
                    itemName: slot.bookingSlot.metadata?.BookingTitle || 'Facility Booking',
                    quantity: 1,
                    unitPrice: slot.price,
                    facilityName: slot.bookingSlot.facilityName,
                    isBooking: true,
                    isDDPayment: false,
                    dateFormatted: bookingDisplayDate.format('DD/MM/YYYY'),
                    timeFormatted: bookingDisplayDate.format('HH:mm'),
                    slotStartTime: slotStart.format('HH:mm'),
                    slotEndTime: slotEnd.format('HH:mm'),
                    slotDateFormatted: slotStart.format('DD/MM/YYYY')
                });
            });
        });

        // Add Direct Debit membership payments — include Completed/Paid and Failed/Rejected only
        // (Pending DD payments are excluded until settled; Cancelled/NoAccount are irrelevant)
        const relevantDdTransactions = ddTransactions.filter(t => {
            const mapped = mapDdStatus(t.paymentStatus);
            return isCompleted(mapped) || isFailed(mapped);
        });
        relevantDdTransactions.forEach((ddTxn, index) => {
            const paymentFrequency = ddTxn.membershipCategory?.paymentFrequency || 'Other';
            const subCategory = resolveMembershipSubCategory(paymentFrequency);
            const paymentDate = dayjs(ddTxn.paymentDate);

            items.push({
                key: `dd-${ddTxn.membershipId}-${ddTxn.runId}-${index}`,
                category: CATEGORY_MEMBERSHIPS_DD,
                subCategory,
                type: 'DirectDebit',
                memberName: ddTxn.memberName,
                membershipId: ddTxn.membershipId,
                amount: ddTxn.amount,
                timestamp: new Date(ddTxn.paymentDate),
                status: mapDdStatus(ddTxn.paymentStatus),
                itemName: `${ddTxn.membershipCategory?.name || 'Membership'} (${paymentFrequency})`,
                quantity: 1,
                unitPrice: ddTxn.amount,
                isBooking: false,
                isDDPayment: true,
                dateFormatted: paymentDate.format('DD/MM/YYYY'),
                timeFormatted: paymentDate.format('HH:mm')
            });
        });

        // Determine which categories need VAT sub-categorization:
        // Only show VAT/Non-VAT buckets if a category contains non-VAT items (i.e. not all-VAT)
        const categoryVatFlags: { [category: string]: { hasVat: boolean; hasNonVat: boolean } } = {};
        items.forEach(item => {
            if (item.vatApplicable === undefined) return;
            if (!categoryVatFlags[item.category]) {
                categoryVatFlags[item.category] = { hasVat: false, hasNonVat: false };
            }
            if (item.vatApplicable) {
                categoryVatFlags[item.category].hasVat = true;
            } else {
                categoryVatFlags[item.category].hasNonVat = true;
            }
        });

        // Apply VAT sub-categories only where there are non-VAT items
        items.forEach(item => {
            if (item.vatApplicable === undefined) return;
            const flags = categoryVatFlags[item.category];
            if (!flags || !flags.hasNonVat) return; // All VAT — no bucketing needed

            const vatSub = resolveVatSubCategory(item.vatApplicable);
            if (vatSub) {
                item.subCategory = item.subCategory
                    ? `${item.subCategory} - ${vatSub}`
                    : vatSub;
            }
        });

        return items.sort((a, b) => dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix());
    }, [transactions, members, memberFrequencies, ddTransactions, fromDate, toDate]);

    // Apply outstanding-only filter when checkbox is ticked (Unpaid, Pending, and Failed)
    const filteredTransactionItems = useMemo(() => {
        if (!showUnpaidOnly) return transactionItems;
        return transactionItems.filter(item => isOutstanding(item.status));
    }, [transactionItems, showUnpaidOnly]);

    // Group data by category
    const groupedData: GroupedData[] = useMemo(() => {
        const groups: { [key: string]: GroupedData } = {};

        filteredTransactionItems.forEach(item => {
            if (!groups[item.category]) {
                groups[item.category] = {
                    category: item.category,
                    totalAmount: 0,
                    completedAmount: 0,
                    unpaidAmount: 0,
                    failedAmount: 0,
                    itemCount: 0,
                    completedCount: 0,
                    unpaidCount: 0,
                    failedCount: 0,
                    items: [],
                    subCategories: {}
                };
            }
            const group = groups[item.category];
            group.totalAmount += item.amount;
            group.itemCount += 1;
            group.items.push(item);

            if (isCompleted(item.status)) {
                group.completedAmount += item.amount;
                group.completedCount += 1;
            } else if (isUnpaid(item.status)) {
                group.unpaidAmount += item.amount;
                group.unpaidCount += 1;
            } else if (isFailed(item.status)) {
                group.failedAmount += item.amount;
                group.failedCount += 1;
            }

            // Track sub-categories
            if (item.subCategory) {
                if (!group.subCategories[item.subCategory]) {
                    group.subCategories[item.subCategory] = { completedAmount: 0, unpaidAmount: 0, failedAmount: 0, completedCount: 0, unpaidCount: 0, failedCount: 0 };
                }
                const sub = group.subCategories[item.subCategory];
                if (isCompleted(item.status)) {
                    sub.completedAmount += item.amount;
                    sub.completedCount += 1;
                } else if (isUnpaid(item.status)) {
                    sub.unpaidAmount += item.amount;
                    sub.unpaidCount += 1;
                } else if (isFailed(item.status)) {
                    sub.failedAmount += item.amount;
                    sub.failedCount += 1;
                }
            }
        });

        // Sort by predefined order
        return CATEGORY_ORDER
            .filter(cat => groups[cat])
            .map(cat => groups[cat]);
    }, [filteredTransactionItems]);

    // Table columns for transaction items
    const itemColumns = [
        {
            title: 'Member',
            dataIndex: 'memberName',
            key: 'memberName',
            width: 200,
            render: (text: string, record: TransactionItem) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{record.memberName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        {record.membershipId}
                    </div>
                </div>
            )
        },
        {
            title: 'Item/Service',
            dataIndex: 'itemName',
            key: 'itemName',
            width: 200,
            render: (text: string, record: TransactionItem) => (
                <div>
                    <div>{record.itemName}</div>
                    {record.subCategory && (
                        <Tag style={{ fontSize: '11px', marginTop: '2px' }}>{record.subCategory}</Tag>
                    )}
                    {record.facilityName && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.facilityName}
                            {record.isBooking && record.slotStartTime && record.slotEndTime && (
                                <div style={{ marginTop: '2px' }}>
                                    {record.slotDateFormatted} {record.slotStartTime} – {record.slotEndTime}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Qty',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 80,
            align: 'right' as const
        },
        {
            title: 'Unit Price',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            width: 120,
            align: 'right' as const,
            render: (value: number) => `${formatCurrency(value)}`
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right' as const,
            render: (value: number) => `${formatCurrency(value)}`
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => {
                const colorMap: Record<string, string> = {
                    'Completed': 'green',
                    'Cancelled': 'red',
                    'Failed': 'red',
                    'Unpaid': 'orange',
                    'Pending': 'blue',
                    'NotApplicable': 'default'
                };
                return (
                    <Tag color={colorMap[status] || 'orange'}>
                        {status}
                    </Tag>
                );
            }
        },
        {
            title: 'Date',
            dataIndex: 'dateFormatted',
            key: 'dateFormatted',
            width: 120
        },
        {
            title: 'Time',
            dataIndex: 'timeFormatted',
            key: 'timeFormatted',
            width: 80
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (text: string, record: TransactionItem) => {
                if (record.isDDPayment) return null;
                return (
                    <Space>
                        <Button
                            data-testid={`recon-edit-${record.membershipId}`}
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => {
                                const originalTransaction = findOriginalTransaction(record.membershipId, record.timestamp);
                                if (originalTransaction) {
                                    setSelectedTransaction(originalTransaction);
                                    setUpdateTransactionModalVisible(true);
                                }
                            }}
                        >
                            Edit
                        </Button>
                        <Button
                            type="text"
                            icon={<PrinterOutlined />}
                            onClick={async () => {
                                const originalTransaction = findOriginalTransaction(record.membershipId, record.timestamp);
                                if (originalTransaction) {
                                    try {
                                        const receiptData = ReceiptPrinterService.createReceiptDataFromTransaction(originalTransaction, record.memberName);
                                        const printSuccess = await receiptPrinterService.printReceipt(receiptData, false);
                                        if (printSuccess) {
                                            message.success('Receipt printed successfully!');
                                        } else {
                                            message.error('Failed to print receipt.');
                                        }
                                    } catch (error) {
                                        console.error('Error printing receipt:', error);
                                        message.error('Failed to print receipt.');
                                    }
                                }
                            }}
                        >
                            Print
                        </Button>
                    </Space>
                );
            }
        }
    ];

    // Calculate summary statistics
    const summary = useMemo(() => {
        const totalTransactions = new Set(transactions.map(t => `${t.timestamp}-${t.membershipId}`)).size;
        const totalAmount = filteredTransactionItems.reduce((sum, item) => sum + item.amount, 0);

        const completedItems = filteredTransactionItems.filter(item => isCompleted(item.status));
        const unpaidItems   = filteredTransactionItems.filter(item => isUnpaid(item.status));
        const failedItems   = filteredTransactionItems.filter(item => isFailed(item.status));

        return {
            totalTransactions,
            totalAmount,
            totalItems: filteredTransactionItems.length,
            completedAmount: completedItems.reduce((sum, item) => sum + item.amount, 0),
            unpaidAmount: unpaidItems.reduce((sum, item) => sum + item.amount, 0),
            failedAmount: failedItems.reduce((sum, item) => sum + item.amount, 0),
            completedCount: completedItems.length,
            unpaidCount: unpaidItems.length,
            failedCount: failedItems.length
        };
    }, [transactions, filteredTransactionItems]);

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Category', 'Sub-Category', 'Member', 'Membership ID', 'Item/Service', 'Quantity', 'Unit Price', 'Amount', 'Status', 'Date', 'Time'];
        const csvContent = [
            headers.join(','),
            ...filteredTransactionItems.map(item => [
                `"${item.category}"`,
                `"${item.subCategory || ''}"`,
                `"${item.memberName}"`,
                item.membershipId,
                `"${item.itemName}"`,
                item.quantity,
                item.unitPrice.toFixed(2),
                item.amount.toFixed(2),
                item.status,
                item.dateFormatted,
                item.timeFormatted
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `reconciliation_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSearch = () => {
        fetchTransactions();
    };

    // Toggle group expansion
    const toggleGroup = (category: string) => {
        setExpandedGroups(prev =>
            prev.includes(category)
                ? prev.filter(t => t !== category)
                : [...prev, category]
        );
    };

    // Toggle item group expansion (within a category)
    const toggleItemGroup = (groupKey: string) => {
        setExpandedItemGroups(prev =>
            prev.includes(groupKey)
                ? prev.filter(k => k !== groupKey)
                : [...prev, groupKey]
        );
    };

    // Compute stats for a group of items
    const computeGroupStats = useCallback((groupItems: TransactionItem[]) => {
        const completedItems = groupItems.filter(i => isCompleted(i.status));
        const unpaidItems   = groupItems.filter(i => isUnpaid(i.status));
        const failedItems   = groupItems.filter(i => isFailed(i.status));
        return {
            completedAmount: completedItems.reduce((sum, i) => sum + i.amount, 0),
            unpaidAmount: unpaidItems.reduce((sum, i) => sum + i.amount, 0),
            failedAmount: failedItems.reduce((sum, i) => sum + i.amount, 0),
            completedCount: completedItems.length,
            unpaidCount: unpaidItems.length,
            failedCount: failedItems.length,
            totalAmount: groupItems.reduce((sum, i) => sum + i.amount, 0),
            totalCount: groupItems.length
        };
    }, []);

    // Compute item/service sub-groups within a category's items
    // For bookings: groups by facility name, with booking title sub-groups within each facility
    // For non-bookings: groups by item name (flat)
    const getItemServiceGroups = useCallback((items: TransactionItem[]) => {
        const groups: { [key: string]: TransactionItem[] } = {};
        items.forEach(item => {
            // Group bookings by facility name, non-bookings by item name
            const groupName = item.isBooking && item.facilityName ? item.facilityName : item.itemName;
            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(item);
        });

        return Object.entries(groups).map(([name, groupItems]) => {
            const stats = computeGroupStats(groupItems);
            const isBookingGroup = groupItems[0]?.isBooking === true;

            // For booking groups, compute sub-groups by booking title
            let subGroups: { name: string; items: TransactionItem[]; completedAmount: number; unpaidAmount: number; failedAmount: number; completedCount: number; unpaidCount: number; failedCount: number; totalAmount: number; totalCount: number }[] | undefined;
            if (isBookingGroup) {
                const titleGroups: { [key: string]: TransactionItem[] } = {};
                groupItems.forEach(item => {
                    const title = item.itemName || 'Facility Booking';
                    if (!titleGroups[title]) titleGroups[title] = [];
                    titleGroups[title].push(item);
                });
                const titles = Object.keys(titleGroups);
                // Only create sub-groups if there are multiple distinct booking titles
                if (titles.length > 1) {
                    subGroups = Object.entries(titleGroups).map(([title, titleItems]) => ({
                        name: title,
                        items: titleItems,
                        ...computeGroupStats(titleItems)
                    })).sort((a, b) => a.name.localeCompare(b.name));
                }
            }

            return {
                name,
                items: groupItems,
                ...stats,
                subGroups
            };
        }).sort((a, b) => a.name.localeCompare(b.name));
    }, [computeGroupStats]);

    // Determine whether a category's items should be split by VAT sub-sections
    const getVatSubSections = useCallback((items: TransactionItem[]): { label: string; items: TransactionItem[] }[] | null => {
        let hasVat = false;
        let hasNonVat = false;
        for (const item of items) {
            if (item.vatApplicable === true) hasVat = true;
            if (item.vatApplicable === false) hasNonVat = true;
            if (hasVat && hasNonVat) break;
        }
        // Only split when there are non-VAT items present
        if (!hasNonVat) return null;

        const vatItems = items.filter(i => i.vatApplicable === true);
        const nonVatItems = items.filter(i => i.vatApplicable === false);
        const otherItems = items.filter(i => i.vatApplicable === undefined);

        const sections: { label: string; items: TransactionItem[] }[] = [];
        if (vatItems.length > 0) sections.push({ label: 'VAT Items', items: vatItems });
        if (nonVatItems.length > 0) sections.push({ label: 'Non-VAT Items', items: nonVatItems });
        if (otherItems.length > 0) sections.push({ label: 'Other Items', items: otherItems });
        return sections;
    }, []);

    // Find original transaction by membershipId and timestamp
    const findOriginalTransaction = (membershipId: string, timestamp: Date): PointOfSaleTransaction | null => {
        return transactions.find(t =>
            t.membershipId === membershipId &&
            t.timestamp.getTime() === timestamp.getTime()
        ) || null;
    };

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Transaction Reconciliation</Title>

            {/* Controls */}
            <Card style={{ marginBottom: '24px' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {/* Quick Date Selection Buttons */}
                    <Space>
                        <span style={{ fontWeight: '500', color: '#666' }}>Quick Select:</span>
                        <Button
                            size="small"
                            onClick={() => {
                                const newFromDate = dayjs();
                                const newToDate = dayjs().add(1, 'day');
                                setFromDate(newFromDate);
                                setToDate(newToDate);
                                fetchTransactions(newFromDate, newToDate);
                            }}
                        >
                            Today
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                const newFromDate = dayjs().startOf('month');
                                const newToDate = dayjs();
                                setFromDate(newFromDate);
                                setToDate(newToDate);
                                fetchTransactions(newFromDate, newToDate);
                            }}
                        >
                            This Month
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                const newFromDate = dayjs().startOf('year');
                                const newToDate = dayjs();
                                setFromDate(newFromDate);
                                setToDate(newToDate);
                                fetchTransactions(newFromDate, newToDate);
                            }}
                        >
                            This Year
                        </Button>
                    </Space>

                    {/* Date Pickers and Action Buttons */}
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space>
                            <DatePicker
                                data-testid="recon-from-date"
                                value={fromDate}
                                onChange={(date) => date && setFromDate(date)}
                                format="DD/MM/YYYY"
                                placeholder="From Date"
                            />
                            <DatePicker
                                data-testid="recon-to-date"
                                value={toDate}
                                onChange={(date) => date && setToDate(date)}
                                format="DD/MM/YYYY"
                                placeholder="To Date"
                            />
                            <Button
                                data-testid="recon-search-btn"
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                loading={loading}
                            >
                                Search
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchTransactions()}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                        </Space>
                        <Space>
                            <Checkbox
                                data-testid="recon-unpaid-only"
                                checked={showUnpaidOnly}
                                onChange={(e) => setShowUnpaidOnly(e.target.checked)}
                            >
                                Show outstanding & failed only
                            </Checkbox>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={exportToCSV}
                                disabled={filteredTransactionItems.length === 0}
                            >
                                Export CSV
                            </Button>
                        </Space>
                    </Space>
                </Space>
            </Card>

            {/* Overall Summary Statistics */}
            <Card title="Overall Summary" style={{ marginBottom: '24px' }}>
                <Row gutter={16}>
                    <Col span={3}>
                        <Statistic
                            title="Total Transactions"
                            value={summary.totalTransactions}
                        />
                    </Col>
                    <Col span={3}>
                        <Statistic
                            title="Total Items"
                            value={summary.totalItems}
                        />
                    </Col>
                    <Col span={4}>
                        <Statistic
                            title="Total Amount"
                            value={summary.totalAmount}
                            precision={2}
                            prefix={currencySymbol}
                        />
                    </Col>
                    <Col span={4}>
                        <Statistic
                            title="Completed"
                            value={summary.completedCount}
                            suffix={`/ ${formatCurrency(summary.completedAmount)}`}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Col>
                    <Col span={3}>
                        <Statistic
                            title="Unpaid"
                            value={summary.unpaidCount}
                            suffix={`/ ${formatCurrency(summary.unpaidAmount)}`}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Col>
                    <Col span={3}>
                        <Statistic
                            title="Failed"
                            value={summary.failedCount}
                            suffix={`/ ${formatCurrency(summary.failedAmount)}`}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Col>
                    <Col span={4}>
                        <Statistic
                            title="Payment Rate"
                            value={summary.totalAmount > 0 ? (summary.completedAmount / summary.totalAmount * 100) : 0}
                            precision={1}
                            suffix="%"
                            valueStyle={{
                                color: summary.totalAmount > 0 && (summary.completedAmount / summary.totalAmount) >= 0.8 ? '#3f8600' : '#cf1322'
                            }}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Category Summary */}
            <Card title="Summary by Category" style={{ marginBottom: '24px' }}>
                <Row gutter={16}>
                    {groupedData.map((catData) => (
                        <Col span={6} key={catData.category} style={{ marginBottom: '16px' }}>
                            <Card size="small">
                                <Statistic
                                    title={catData.category}
                                    value={catData.completedAmount}
                                    precision={2}
                                    prefix={currencySymbol}
                                    valueStyle={{ color: '#3f8600' }}
                                />
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                    {formatNumber(catData.completedCount)} completed items
                                </div>
                                {catData.unpaidAmount > 0 && (
                                    <div style={{ fontSize: '12px', color: '#cf1322', marginTop: '4px' }}>
                                        {formatCurrency(catData.unpaidAmount)} unpaid ({formatNumber(catData.unpaidCount)} items)
                                    </div>
                                )}
                                {/* Sub-category breakdown */}
                                {Object.keys(catData.subCategories).length > 0 && (
                                    <div style={{ marginTop: '8px', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
                                        {Object.entries(catData.subCategories).map(([subCat, data]) => (
                                            <div key={subCat} style={{ fontSize: '11px', marginBottom: '4px' }}>
                                                <div style={{ fontWeight: 500, color: '#555' }}>{subCat}</div>
                                                {data.completedCount > 0 && (
                                                    <div style={{ color: '#3f8600', paddingLeft: '8px' }}>
                                                        {formatCurrency(data.completedAmount)} ({formatNumber(data.completedCount)})
                                                    </div>
                                                )}
                                                {data.unpaidCount > 0 && (
                                                    <div style={{ color: '#cf1322', paddingLeft: '8px' }}>
                                                        {formatCurrency(data.unpaidAmount)} unpaid ({formatNumber(data.unpaidCount)})
                                                    </div>
                                                )}
                                                {data.failedCount > 0 && (
                                                    <div style={{ color: '#cf1322', paddingLeft: '8px' }}>
                                                        {formatCurrency(data.failedAmount)} failed ({formatNumber(data.failedCount)})
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Card>

            {/* Grouped Transaction Details */}
            <Card title="Transaction Details (Grouped by Category)">
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size="large" tip="Loading transactions..." />
                    </div>
                ) : filteredTransactionItems.length === 0 ? (
                    <Alert
                        message="No transactions found"
                        description="No transactions were found for the selected date range."
                        type="info"
                        showIcon
                    />
                ) : (
                    <div>
                        {groupedData.map((group) => (
                            <Card
                                key={group.category}
                                size="small"
                                style={{ marginBottom: '16px' }}
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div>
                                            <span style={{ fontWeight: 'bold' }}>{group.category}</span>
                                            <span style={{ marginLeft: '16px', color: '#666' }}>
                                                {formatNumber(group.completedCount)} completed
                                            </span>
                                            <span style={{ marginLeft: '8px', color: '#3f8600' }}>
                                                {formatCurrency(group.completedAmount)}
                                            </span>
                                            {group.unpaidAmount > 0 && (
                                                <span style={{ marginLeft: '8px', color: '#cf1322' }}>
                                                    {formatCurrency(group.unpaidAmount)} unpaid ({formatNumber(group.unpaidCount)})
                                                </span>
                                            )}
                                            {group.failedCount > 0 && (
                                                <span style={{ marginLeft: '8px', color: '#cf1322' }}>
                                                    {formatCurrency(group.failedAmount)} failed ({formatNumber(group.failedCount)})
                                                </span>
                                            )}
                                        </div>
                                        <Button
                                            data-testid={`recon-group-toggle-${group.category}`}
                                            type="text"
                                            icon={expandedGroups.includes(group.category) ? <CaretDownOutlined /> : <CaretRightOutlined />}
                                            onClick={() => toggleGroup(group.category)}
                                        >
                                            {expandedGroups.includes(group.category) ? 'Collapse' : 'Expand'}
                                        </Button>
                                    </div>
                                }
                            >
                                {expandedGroups.includes(group.category) && (() => {
                                    const vatSections = getVatSubSections(group.items);

                                    // Render a collapsible item group header + table
                                    const renderGroupRow = (groupKey: string, name: string, stats: { totalCount: number; completedAmount: number; unpaidCount: number; unpaidAmount: number; failedCount: number; failedAmount: number }, items: TransactionItem[], bgColor = '#fafafa') => {
                                        const isExpanded = expandedItemGroups.includes(groupKey);
                                        return (
                                            <div
                                                key={groupKey}
                                                style={{
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: '4px',
                                                    marginBottom: '8px',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <div
                                                    data-testid={`recon-item-group-${groupKey}`}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '8px 16px',
                                                        backgroundColor: bgColor,
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => toggleItemGroup(groupKey)}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                                                        <span style={{ fontWeight: 500 }}>{name}</span>
                                                        <span style={{ color: '#666', fontSize: '12px' }}>
                                                            {formatNumber(stats.totalCount)} item{stats.totalCount !== 1 ? 's' : ''}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ color: '#3f8600', fontWeight: 500 }}>
                                                            {formatCurrency(stats.completedAmount)}
                                                        </span>
                                                        {stats.unpaidCount > 0 && (
                                                            <Tag color="orange" style={{ margin: 0 }}>
                                                                {formatCurrency(stats.unpaidAmount)} unpaid ({formatNumber(stats.unpaidCount)})
                                                            </Tag>
                                                        )}
                                                        {stats.failedCount > 0 && (
                                                            <Tag color="red" style={{ margin: 0 }}>
                                                                {formatCurrency(stats.failedAmount)} failed ({formatNumber(stats.failedCount)})
                                                            </Tag>
                                                        )}
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <Table
                                                        dataSource={items}
                                                        columns={itemColumns}
                                                        pagination={false}
                                                        size="small"
                                                        rowClassName={(record) =>
                                                            isFailed(record.status) ? 'failed-row' : isUnpaid(record.status) ? 'unpaid-row' : ''
                                                        }
                                                        scroll={{ x: 1200 }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    };

                                    const renderItemGroups = (items: TransactionItem[], keyPrefix: string) =>
                                        getItemServiceGroups(items).map((itemGroup) => {
                                            const itemGroupKey = `${keyPrefix}::${itemGroup.name}`;

                                            // If this group has booking title sub-groups, render facility as parent with sub-groups inside
                                            if (itemGroup.subGroups) {
                                                const isFacilityExpanded = expandedItemGroups.includes(itemGroupKey);
                                                return (
                                                    <div
                                                        key={itemGroupKey}
                                                        style={{
                                                            border: '1px solid #f0f0f0',
                                                            borderRadius: '4px',
                                                            marginBottom: '8px',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        {/* Facility header */}
                                                        <div
                                                            data-testid={`recon-item-group-${itemGroupKey}`}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '8px 16px',
                                                                backgroundColor: '#fafafa',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => toggleItemGroup(itemGroupKey)}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                {isFacilityExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                                                                <span style={{ fontWeight: 500 }}>{itemGroup.name}</span>
                                                                <span style={{ color: '#666', fontSize: '12px' }}>
                                                                    {formatNumber(itemGroup.totalCount)} item{itemGroup.totalCount !== 1 ? 's' : ''}
                                                                </span>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                <span style={{ color: '#3f8600', fontWeight: 500 }}>
                                                                    {formatCurrency(itemGroup.completedAmount)}
                                                                </span>
                                                                {itemGroup.unpaidCount > 0 && (
                                                                    <Tag color="orange" style={{ margin: 0 }}>
                                                                        {formatCurrency(itemGroup.unpaidAmount)} unpaid ({formatNumber(itemGroup.unpaidCount)})
                                                                    </Tag>
                                                                )}
                                                                {itemGroup.failedCount > 0 && (
                                                                    <Tag color="red" style={{ margin: 0 }}>
                                                                        {formatNumber(itemGroup.failedCount)} failed
                                                                    </Tag>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* Booking title sub-groups */}
                                                        {isFacilityExpanded && (
                                                            <div style={{ padding: '8px 8px 8px 24px' }}>
                                                                {itemGroup.subGroups.map(subGroup => {
                                                                    const subGroupKey = `${itemGroupKey}::${subGroup.name}`;
                                                                    return renderGroupRow(subGroupKey, subGroup.name, subGroup, subGroup.items, '#f5f5f5');
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }

                                            // Non-booking or single-title booking: render flat as before
                                            return renderGroupRow(itemGroupKey, itemGroup.name, itemGroup, itemGroup.items);
                                        });

                                    return vatSections ? (
                                        <div>
                                            {vatSections.map(section => {
                                                const sectionKey = `${group.category}::vat::${section.label}`;
                                                const isSectionExpanded = expandedItemGroups.includes(sectionKey);
                                                const sectionCompleted = section.items.filter(i => isCompleted(i.status));
                                                const sectionUnpaid   = section.items.filter(i => isUnpaid(i.status));
                                                const sectionFailed   = section.items.filter(i => isFailed(i.status));
                                                const sectionCompletedAmount = sectionCompleted.reduce((sum, i) => sum + i.amount, 0);
                                                const sectionUnpaidAmount = sectionUnpaid.reduce((sum, i) => sum + i.amount, 0);
                                                const sectionFailedAmount = sectionFailed.reduce((sum, i) => sum + i.amount, 0);
                                                return (
                                                    <div key={sectionKey} style={{ marginBottom: '12px' }}>
                                                        <div
                                                            data-testid={`recon-item-group-${sectionKey}`}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '6px 12px',
                                                                backgroundColor: '#e6f7ff',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                marginBottom: '8px'
                                                            }}
                                                            onClick={() => toggleItemGroup(sectionKey)}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                {isSectionExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                                                                <span style={{ fontWeight: 600 }}>{section.label}</span>
                                                                <span style={{ color: '#666', fontSize: '12px' }}>
                                                                    {formatNumber(section.items.length)} item{section.items.length !== 1 ? 's' : ''}
                                                                </span>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                <span style={{ color: '#3f8600', fontWeight: 500 }}>
                                                                    {formatCurrency(sectionCompletedAmount)}
                                                                </span>
                                                                {sectionUnpaid.length > 0 && (
                                                                    <Tag color="orange" style={{ margin: 0 }}>
                                                                        {formatCurrency(sectionUnpaidAmount)} unpaid ({formatNumber(sectionUnpaid.length)})
                                                                    </Tag>
                                                                )}
                                                                {sectionFailed.length > 0 && (
                                                                    <Tag color="red" style={{ margin: 0 }}>
                                                                        {formatCurrency(sectionFailedAmount)} failed ({formatNumber(sectionFailed.length)})
                                                                    </Tag>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {isSectionExpanded && (
                                                            <div style={{ paddingLeft: '16px' }}>
                                                                {renderItemGroups(section.items, `${group.category}::${section.label}`)}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div>
                                            {renderItemGroups(group.items, group.category)}
                                        </div>
                                    );
                                })()}
                            </Card>
                        ))}
                    </div>
                )}
            </Card>

            {/* Update Transaction Modal */}
            {updateTransactionModalVisible && selectedTransaction && (
                <UpdateTransactionModal
                    visible={updateTransactionModalVisible}
                    transaction={selectedTransaction}
                    onCancel={() => setUpdateTransactionModalVisible(false)}
                    onSuccess={() => {
                        setUpdateTransactionModalVisible(false);
                        fetchTransactions();
                    }}
                />
            )}
        </div>
    );
};

export default TransactionReconciliation;
